import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Tag, Wrench, Utensils } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/components/marketplace/shared";

const KIND = {
  product: { label: "Produto", icon: Tag, to: (x) => `/marketplace/produto/${x.id}` },
  service: { label: "Serviço", icon: Wrench, to: (x) => `/marketplace/servico/${x.id}` },
  restaurant: { label: "Restaurante", icon: Utensils, to: (x) => `/restaurante/${x.id}` },
};

function normalise(kind, x) {
  if (kind === "product") return { kind, id: x.id, title: x.name, image: x.photos?.[0], city: x.city, seller_id: x.seller_id, price: x.price_minor, created: x.created_at };
  if (kind === "service") return { kind, id: x.id, title: x.title, image: x.images?.[0] || x.photos?.[0], city: x.location || x.city, seller_id: x.seller_id, price: x.service_type === "fixed_price" ? x.price_minor : null, created: x.created_at };
  return { kind, id: x.id, title: x.shop_name, image: x.photos?.[0] || x.logo_url, city: x.city, seller_id: x.id, subtitle: x.restaurant?.cuisine?.[0], created: x.created_at };
}

export function useDiscoverItems(limit = 8) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    Promise.allSettled([
      api.get("/products", { params: { limit: 6 } }),
      api.get("/services", { params: { limit: 4 } }),
      api.get("/restaurants"),
      api.get("/directory", { params: { featured: true, limit: 20 } }),
    ]).then(([p, s, r, f]) => {
      const featured = new Set(f.status === "fulfilled" ? (f.value.data.items || []).map(b => b.id) : []);
      const all = [
        ...(p.status === "fulfilled" ? p.value.data : []).map(x => normalise("product", x)),
        ...(s.status === "fulfilled" ? s.value.data : []).map(x => normalise("service", x)),
        ...(r.status === "fulfilled" ? r.value.data : []).slice(0, 4).map(x => normalise("restaurant", x)),
      ].map(x => ({ ...x, featured: featured.has(x.seller_id) }));
      all.sort((a, b) => (b.featured - a.featured) || String(b.created || "").localeCompare(String(a.created || "")));
      setItems(all.slice(0, limit));
    });
  }, [limit]);
  return items;
}

export function DiscoverCard({ item }) {
  const k = KIND[item.kind]; const Icon = k.icon;
  return (
    <Link to={k.to(item)} data-testid={`home-discover-${item.kind}-${item.id}`} data-featured={item.featured ? "1" : "0"}
          className="snap-start shrink-0 w-56 rounded-2xl overflow-hidden bg-white border border-zinc-200 card-hover">
      <div className="relative aspect-square bg-zinc-100">
        {item.image && <img src={item.image} alt={item.title} className="w-full h-full object-cover" />}
        <span className="absolute top-2 left-2 chip !py-1 !px-2 text-[10px] flex items-center gap-1"><Icon size={10} /> {k.label}</span>
      </div>
      <div className="p-3">
        <div className="font-semibold text-sm leading-tight mb-1 line-clamp-2">{item.title}</div>
        <div className="text-xs text-zinc-500">{item.price != null ? money(item.price) : (item.subtitle || "")}</div>
        {item.city && <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {item.city}</div>}
      </div>
    </Link>
  );
}
